import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { signup, requestEmailVerification } from '@/utils/auth-pb';
import { checkEmailTypo } from '@/utils/email-typo-check';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { AuthInput } from '@/components/ui/auth-input';
import { PasswordInput } from '@/components/ui/password-input';
import { AuthCheckbox } from '@/components/ui/auth-checkbox';
import LocalizedLink from '@/components/shared/LocalizedLink';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import { useLanguage } from '@/i18n/LanguageContext';

export default function RegisterPage() {
  const { t } = useTranslation('auth');
  const { lang } = useLanguage();
  const navigate = useLocalizedNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [marketingAccepted, setMarketingAccepted] = useState(true);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const emailTypoHint = checkEmailTypo(email);
  const passwordsMatch = password === confirmPassword;
  const passwordTooShort = password.length > 0 && password.length < 8;
  const passwordError = confirmPassword.length > 0 && !passwordsMatch;
  const isFormValid =
    name.trim() !== '' &&
    email.trim() !== '' &&
    password.length >= 8 &&
    passwordsMatch &&
    agreementAccepted;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    setError('');
    setIsLoading(true);

    try {
      await signup({
        email,
        password,
        name,
        marketingConsent: marketingAccepted,
      });

      // Запрашиваем отправку письма верификации
      await requestEmailVerification(email);

      // Переходим на страницу подтверждения email
      navigate(`/verify-email?email=${encodeURIComponent(email)}`);
    } catch (err) {
      setError((err as { message: string }).message || t('register.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout title={t('register.title')}>
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="rounded-[12px] bg-[#fff0f0] p-4 text-[14px] text-[#ff5b63]">
            {error}
          </div>
        )}

        <AuthInput
          id="name"
          type="text"
          label={t('register.nameLabel')}
          placeholder={t('register.namePlaceholder')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          disabled={isLoading}
        />

        <AuthInput
          id="email"
          type="email"
          label={t('register.emailLabel')}
          placeholder={t('register.emailPlaceholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={isLoading}
        />
        {emailTypoHint && (
          <p className="text-[12px] text-[#f59e0b]">{t('emailTypoCheck.suggestion', { suggestion: emailTypoHint, ns: 'common' })}</p>
        )}

        <div className="space-y-2">
          <label
            htmlFor="password"
            className="block text-[14px] font-medium text-[#0d0d0d]"
          >
            {t('register.passwordLabel')}
          </label>
          <PasswordInput
            id="password"
            placeholder={t('register.passwordPlaceholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            disabled={isLoading}
            error={passwordTooShort}
          />
          {passwordTooShort && (
            <p className="text-[12px] text-[#ff5b63]">{t('register.passwordTooShort')}</p>
          )}
        </div>

        <div className="space-y-2">
          <label
            htmlFor="confirmPassword"
            className="block text-[14px] font-medium text-[#0d0d0d]"
          >
            {t('register.confirmPasswordLabel')}
          </label>
          <PasswordInput
            id="confirmPassword"
            placeholder={t('register.confirmPasswordPlaceholder')}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            disabled={isLoading}
            error={passwordError}
          />
          {passwordError && (
            <p className="text-[12px] text-[#ff5b63]">{t('register.passwordsMismatch')}</p>
          )}
        </div>

        <div className="space-y-4 pt-2">
          <AuthCheckbox
            id="agreement"
            checked={agreementAccepted}
            onChange={(e) => setAgreementAccepted(e.target.checked)}
            label={
              <>
                {t('register.agreementPrefix')}{' '}
                <a
                  href={lang === 'en' ? 'https://create-products.com/en/terms' : 'https://create-products.com/terms'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#2197f1] hover:underline"
                >
                  {t('register.termsOfService')}
                </a>{' '}
                {t('register.and')}{' '}
                <a
                  href={lang === 'en' ? 'https://create-products.com/en/privacy-policy' : 'https://create-products.com/privacy-policy'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#2197f1] hover:underline"
                >
                  {t('register.privacyPolicy')}
                </a>
              </>
            }
          />

          <AuthCheckbox
            id="marketing"
            checked={marketingAccepted}
            onChange={(e) => setMarketingAccepted(e.target.checked)}
            label={t('register.marketingConsent')}
          />
        </div>

        <button
          type="submit"
          disabled={!isFormValid || isLoading}
          className="mt-6 h-[52px] w-full rounded-[12px] text-[16px] font-semibold text-white transition-colors disabled:cursor-not-allowed"
          style={{
            backgroundColor: isFormValid ? '#2197f1' : 'rgba(33, 151, 241, 0.2)',
          }}
        >
          {isLoading ? t('register.submitting') : t('register.submit')}
        </button>

        <p className="pt-4 text-center text-[14px] text-[#565858]">
          {t('register.hasAccount')}{' '}
          <LocalizedLink to="/login" className="font-semibold text-[#2197f1] hover:underline">
            {t('register.login')}
          </LocalizedLink>
        </p>
      </form>
    </AuthLayout>
  );
}
